import {CommonModule} from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
    inject
} from '@angular/core';
import {I18NextPipe} from '@kokos-i18next';
import {I18nRerenderOnLanguageChangeDirective} from '../../../../directives/i18n-rerender-on-language-change.directive';
import {fileSizeToMbLabel} from '../../../../utils/file.util';
import {HinweisboxComponent} from '../../../hinweisbox/hinweisbox.component';
import {HinweisboxTypeEnum} from '../../../hinweisbox/hinweisbox-type.enum';
import {LinkOutComponent} from '../../../structure/link-out/link-out.component';
import {UploadFile} from './upload-file';
import {FileUploadAnnouncerService} from './file-upload-announcer.service';

@Component({
    selector: 'pf-file-upload',
    imports: [CommonModule, HinweisboxComponent, LinkOutComponent, I18NextPipe, I18nRerenderOnLanguageChangeDirective],
    templateUrl: './file-upload.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class FileUploadComponent implements OnChanges {
    private readonly announcer = inject(FileUploadAnnouncerService);

    @Input() uploadFiles: UploadFile[] = [];
    @Input() uploadInstructions!: string;
    @Input() disabled = false;

    @Output() filesSelected = new EventEmitter<FileList>();
    @Output() remove = new EventEmitter<number>();
    @Output() openFile = new EventEmitter<number>();

    @ViewChild('uploadStatusAlert', {static: true})
    private readonly uploadStatusAlert!: ElementRef<HTMLElement>;

    isDragOver = false;
    isCollapsed = true;

    protected readonly HinweisboxTypeEnum = HinweisboxTypeEnum;
    protected readonly fileSizeToMbLabel = fileSizeToMbLabel;

    ngOnChanges(changes: SimpleChanges): void {
        const change = changes['uploadFiles'];
        if (!change?.previousValue) return;

        this.announcer.announceUploadStatusChange(
            this.uploadStatusAlert.nativeElement,
            change.previousValue as UploadFile[],
            change.currentValue as UploadFile[]
        );
    }

    toggleCollapse(): void {
        this.isCollapsed = !this.isCollapsed;
    }

    onFileSelected(event: Event) {
        if (this.disabled) return;
        const input = event.target as HTMLInputElement;
        if (input.files?.length) {
            this.filesSelected.emit(input.files);
            input.value = '';
        }
    }

    onDrop(event: DragEvent) {
        event.preventDefault();
        this.isDragOver = false;
        if (this.disabled) return;
        if (event.dataTransfer?.files?.length) {
            this.filesSelected.emit(event.dataTransfer.files);
        }
    }

    onDragOver(event: DragEvent) {
        event.preventDefault();
        if (this.disabled) return;
        this.isDragOver = true;
    }

    onDragLeave() {
        this.isDragOver = false;
    }

    onRemove(index: number): void {
        const file = this.uploadFiles[index];
        if (file) {
            this.announcer.announceFileDeleted(this.uploadStatusAlert.nativeElement, file);
        }
        this.remove.emit(index);
    }

    onOpen(index: number, event: MouseEvent): void {
        event.preventDefault();
        this.openFile.emit(index);
    }
}